import type { CtcaeTerm, CtcaeVersion, SearchResult } from "../types";
import type {
  CatalogInput,
  EncoderMatch,
  EncoderModelSpec,
  IndexSource,
  IndexValidationSummary,
  PrecomputedIndexManifest,
} from "./types";

export const INDEX_SCHEMA_VERSION = 3;
export const INDEX_ARTIFACT_SCHEMA_VERSION = 2;
export const RELIABILITY_GATED_INDEX_SCHEMA_VERSION = 4;
export const RELIABILITY_GATED_ARTIFACT_SCHEMA_VERSION = 3;
export const EMBEDDING_CONTRACT = {
  pooling: "cls",
  normalize: true,
  truncation: true,
  maxLength: 192,
} as const;

export type IndexContract = {
  schemaVersion: number;
  modelKey: string;
  modelRevision: string;
  artifactSha256?: string;
  version: CtcaeVersion;
  catalogFingerprint: string;
  ids: string[];
  codes: string[];
  dimensions: number;
  representationsPerTarget?: number;
  matrix: Float32Array | ArrayBuffer;
  scoring?: {
    type: "target_reliability_gated_anchor_prototype_blend";
    prototypeTemperature: number;
    residualWeights: Float32Array | ArrayBuffer;
    queryAlignment: Float32Array | ArrayBuffer;
  };
  source: IndexSource;
};

export type IndexExpectation = {
  modelKey: string;
  modelRevision: string;
  artifactSha256?: string;
  version: CtcaeVersion;
  catalogFingerprint: string;
  records: CatalogInput[];
  source: IndexSource;
};

export type SelfRetrievalCheck = {
  valid: boolean;
  rows: number[];
  failures: number[];
};

export type IndexValidationResult = {
  valid: boolean;
  reason?: string;
  matrix?: Float32Array;
  summary?: IndexValidationSummary;
  selfRetrieval?: SelfRetrievalCheck;
};

export type CanonicalIntegrityCase = {
  query: string;
  limit: number;
  expectedFirstId?: string;
  requiredIds: string[];
  forbiddenIds: string[];
};

export function encoderDocument(record: CtcaeTerm) {
  const parts = [`CTCAE term: ${record.term}`, `Category: ${record.soc}`];
  if (record.definition?.trim()) parts.push(`Definition: ${record.definition.trim()}`);
  return parts.join(". ");
}

function catalogSerialization(records: { id?: string; code: string; text: string }[]) {
  return records
    .map((record) => `${record.id ?? ""}\u0000${record.code}\u0000${record.text}\u0001`)
    .join("");
}

/**
 * A compact 64-bit FNV-1a digest is used in cache keys and manifests. The
 * complete ordered id/code/text list is still checked when an index is read;
 * this digest only avoids putting that list in every IndexedDB key.
 */
export function catalogFingerprint(records: { id?: string; code: string; text: string }[]) {
  let hash = 0xcbf29ce484222325n;
  for (const value of catalogSerialization(records)) {
    hash ^= BigInt(value.charCodeAt(0));
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return hash.toString(16).padStart(16, "0");
}

function asFloat32Array(matrix: Float32Array | ArrayBuffer) {
  if (matrix instanceof Float32Array) return matrix;
  if (matrix.byteLength % Float32Array.BYTES_PER_ELEMENT !== 0) return undefined;
  return new Float32Array(matrix);
}

function exactOrderedValues<T>(actual: T[], expected: T[]) {
  return actual.length === expected.length && actual.every((value, index) => value === expected[index]);
}

export function selfRetrievalSmoke(
  index: Pick<IndexContract, "dimensions" | "matrix" | "representationsPerTarget">,
  sampleRows?: number[],
): SelfRetrievalCheck {
  const matrix = asFloat32Array(index.matrix);
  if (!matrix || !Number.isInteger(index.dimensions) || index.dimensions <= 0) {
    return { valid: false, rows: [], failures: [] };
  }
  const representations = index.representationsPerTarget ?? 1;
  const rowCount = matrix.length / (index.dimensions * representations);
  const rows = [...new Set(sampleRows ?? [0, Math.floor(rowCount / 2), rowCount - 1])]
    .filter((row) => row >= 0 && row < rowCount);
  const failures: number[] = [];
  for (const row of rows) {
    let ownScore = Number.NEGATIVE_INFINITY;
    let bestScore = Number.NEGATIVE_INFINITY;
    let bestRow = -1;
    const rowOffset = row * representations * index.dimensions;
    for (let candidate = 0; candidate < rowCount; candidate += 1) {
      let score = 0;
      const candidateOffset = candidate * representations * index.dimensions;
      for (let column = 0; column < index.dimensions; column += 1) {
        score += matrix[rowOffset + column] * matrix[candidateOffset + column];
      }
      if (candidate === row) ownScore = score;
      if (score > bestScore) {
        bestScore = score;
        bestRow = candidate;
      }
    }
    if (bestRow !== row && ownScore + 1e-4 < bestScore) failures.push(row);
  }
  return { valid: failures.length === 0, rows, failures };
}

export function validateIndexContract(index: IndexContract, expected: IndexExpectation): IndexValidationResult {
  const representations = index.representationsPerTarget ?? 1;
  const expectedSchema = index.scoring ? RELIABILITY_GATED_INDEX_SCHEMA_VERSION : INDEX_SCHEMA_VERSION;
  if (index.schemaVersion !== expectedSchema) {
    return { valid: false, reason: `index schema ${index.schemaVersion} is not ${expectedSchema}` };
  }
  if (index.modelKey !== expected.modelKey || index.modelRevision !== expected.modelRevision) {
    return { valid: false, reason: "index model identity does not match the active model" };
  }
  if (index.artifactSha256 !== expected.artifactSha256) {
    return { valid: false, reason: "index model artifact checksum does not match the active model" };
  }
  if (index.version !== expected.version) {
    return { valid: false, reason: `index version ${index.version} does not match v${expected.version}` };
  }
  if (index.catalogFingerprint !== expected.catalogFingerprint) {
    return { valid: false, reason: "index catalog fingerprint does not match the source catalog" };
  }
  if (index.source !== expected.source) {
    return { valid: false, reason: `index source ${index.source} does not match ${expected.source}` };
  }

  const expectedIds = expected.records.map((record) => record.id);
  const expectedCodes = expected.records.map((record) => record.code);
  if (!exactOrderedValues(index.ids, expectedIds)) {
    return { valid: false, reason: "index record-id order does not match the source catalog" };
  }
  if (!exactOrderedValues(index.codes, expectedCodes)) {
    return { valid: false, reason: "index LLT-code order does not match the source catalog" };
  }
  if (new Set(index.ids).size !== index.ids.length || new Set(index.codes).size !== index.codes.length) {
    return { valid: false, reason: "index contains duplicate canonical record ids or LLT codes" };
  }
  if (index.ids.length !== expected.records.length || index.codes.length !== expected.records.length) {
    return { valid: false, reason: "index row count does not match the source catalog" };
  }
  if (!Number.isInteger(index.dimensions) || index.dimensions <= 0) {
    return { valid: false, reason: `index dimensions are invalid: ${index.dimensions}` };
  }

  const matrix = asFloat32Array(index.matrix);
  if (!Number.isInteger(representations) || representations < 1) {
    return { valid: false, reason: "index representation count is invalid" };
  }
  const expectedLength = expected.records.length * representations * index.dimensions;
  if (!matrix || matrix.length !== expectedLength || matrix.byteLength !== expectedLength * Float32Array.BYTES_PER_ELEMENT) {
    return {
      valid: false,
      reason: `index matrix shape is invalid: expected ${expectedLength} float32 values, received ${matrix?.length ?? "non-float32 data"}`,
    };
  }
  for (let row = 0; row < expected.records.length; row += 1) {
    for (let representation = 0; representation < representations; representation += 1) {
      let normSquared = 0;
      const offset = (row * representations + representation) * index.dimensions;
      for (let column = 0; column < index.dimensions; column += 1) {
        const value = matrix[offset + column];
        if (!Number.isFinite(value)) return { valid: false, reason: `index matrix contains a non-finite value at row ${row}` };
        normSquared += value * value;
      }
      if (normSquared <= 1e-12) return { valid: false, reason: `index matrix contains an empty embedding at row ${row}` };
    }
  }

  if (index.scoring) {
    if (representations < 2 || index.scoring.type !== "target_reliability_gated_anchor_prototype_blend") {
      return { valid: false, reason: "index reliability-gated scoring representation is invalid" };
    }
    if (!(index.scoring.prototypeTemperature > 0)) {
      return { valid: false, reason: "index prototype temperature is invalid" };
    }
    const weights = asFloat32Array(index.scoring.residualWeights);
    const alignment = asFloat32Array(index.scoring.queryAlignment);
    if (!weights || weights.length !== expected.records.length || weights.some((value) => !Number.isFinite(value) || value <= 0 || value > 1)) {
      return { valid: false, reason: "index residual weights are invalid" };
    }
    if (!alignment || alignment.length !== index.dimensions * index.dimensions || alignment.some((value) => !Number.isFinite(value))) {
      return { valid: false, reason: "index query alignment is invalid" };
    }
    index.scoring.residualWeights = weights;
    index.scoring.queryAlignment = alignment;
  }

  const selfRetrieval = selfRetrievalSmoke({ dimensions: index.dimensions, matrix, representationsPerTarget: representations });
  if (!selfRetrieval.valid) {
    return {
      valid: false,
      reason: `index self-retrieval smoke failed at rows ${selfRetrieval.failures.join(", ")}`,
      selfRetrieval,
    };
  }
  return {
    valid: true,
    matrix,
    selfRetrieval,
    summary: {
      schemaVersion: index.schemaVersion,
      modelKey: index.modelKey,
      modelRevision: index.modelRevision,
      artifactSha256: index.artifactSha256,
      version: index.version,
      catalogFingerprint: index.catalogFingerprint,
      rows: index.codes.length,
      dimensions: index.dimensions,
      source: expected.source,
      selfRetrievalRows: selfRetrieval.rows.length,
      representationsPerTarget: representations,
      scoringType: index.scoring?.type,
    },
  };
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

export function validatePrecomputedManifest(
  value: unknown,
  model: EncoderModelSpec,
  expectation: Omit<IndexExpectation, "source">,
): { valid: true; manifest: PrecomputedIndexManifest } | { valid: false; reason: string } {
  if (!value || typeof value !== "object") return { valid: false, reason: "index manifest is not an object" };
  const manifest = value as Partial<PrecomputedIndexManifest>;
  const reliabilityGated = manifest.scoring?.type === "target_reliability_gated_anchor_prototype_blend";
  const artifactSchema = reliabilityGated ? RELIABILITY_GATED_ARTIFACT_SCHEMA_VERSION : INDEX_ARTIFACT_SCHEMA_VERSION;
  const indexSchema = reliabilityGated ? RELIABILITY_GATED_INDEX_SCHEMA_VERSION : INDEX_SCHEMA_VERSION;
  if (manifest.schemaVersion !== artifactSchema) {
    return { valid: false, reason: `artifact schema ${manifest.schemaVersion} is not ${artifactSchema}` };
  }
  if (manifest.indexSchemaVersion !== indexSchema) {
    return { valid: false, reason: `artifact index schema ${manifest.indexSchemaVersion} is not ${indexSchema}` };
  }
  if (manifest.modelKey !== model.key || manifest.modelId !== model.modelId || manifest.revision !== model.revision) {
    return { valid: false, reason: "artifact model identity does not match the active model" };
  }
  if (manifest.artifactSha256 !== model.artifactSha256) {
    return { valid: false, reason: "artifact model checksum does not match the active model" };
  }
  if (manifest.rankingArtifactSha256 !== model.rankingArtifactSha256) {
    return { valid: false, reason: "artifact ranking checksum does not match the active model" };
  }
  if (model.dtype !== "auto" && manifest.generatedDtype !== model.dtype) {
    return { valid: false, reason: "artifact embedding dtype does not match the configured model" };
  }
  if (manifest.version !== expectation.version || manifest.catalogFingerprint !== expectation.catalogFingerprint) {
    return { valid: false, reason: "artifact catalog version or fingerprint does not match" };
  }
  const expectedIds = expectation.records.map((record) => record.id);
  const expectedCodes = expectation.records.map((record) => record.code);
  if (!isStringArray(manifest.orderedIds) || !exactOrderedValues(manifest.orderedIds, expectedIds)) {
    return { valid: false, reason: "artifact record-id order does not match the source catalog" };
  }
  if (!isStringArray(manifest.orderedCodes) || !exactOrderedValues(manifest.orderedCodes, expectedCodes)) {
    return { valid: false, reason: "artifact LLT-code order does not match the source catalog" };
  }
  if (manifest.rows !== expectation.records.length || !Number.isInteger(manifest.dimensions) || (manifest.dimensions ?? 0) <= 0) {
    return { valid: false, reason: "artifact row count or dimensions are invalid" };
  }
  const representations = manifest.representationsPerTarget ?? 1;
  const expectedBytes = manifest.rows * representations * manifest.dimensions! * Float32Array.BYTES_PER_ELEMENT;
  if (manifest.matrixBytes !== expectedBytes || manifest.matrixDtype !== "float32" || manifest.byteOrder !== "little-endian") {
    return { valid: false, reason: "artifact matrix shape or representation is invalid" };
  }
  if (!manifest.matrixFile || !manifest.matrixSha256 || !manifest.queryMatrixFile || !manifest.queryMatrixSha256) {
    return { valid: false, reason: "artifact matrix provenance is incomplete" };
  }
  if (reliabilityGated) {
    const scoring = manifest.scoring!;
    if (
      representations !== 4
      || scoring.anchorRepresentation !== 0
      || !exactOrderedValues(scoring.prototypeRepresentations ?? [], [1, 2, 3])
      || !(scoring.prototypeTemperature > 0)
      || scoring.residualWeightsBytes !== manifest.rows * Float32Array.BYTES_PER_ELEMENT
      || scoring.queryAlignmentBytes !== manifest.dimensions! * manifest.dimensions! * Float32Array.BYTES_PER_ELEMENT
      || !scoring.residualWeightsFile
      || !scoring.residualWeightsSha256
      || !scoring.queryAlignmentFile
      || !scoring.queryAlignmentSha256
      || scoring.queryAlignmentMethod !== "pooled_catalog_orthogonal_procrustes"
    ) {
      return { valid: false, reason: "artifact reliability-gated scoring contract is invalid" };
    }
  }
  if (!Array.isArray(manifest.regressions) || !manifest.regressions.length) {
    return { valid: false, reason: "artifact canonical regression provenance is missing" };
  }
  if (
    manifest.queryMatrixBytes !== manifest.regressions.length * manifest.dimensions! * Float32Array.BYTES_PER_ELEMENT
    || manifest.regressions.some((test, row) => test.queryRow !== row || !test.query || !isStringArray(test.requiredIds) || !isStringArray(test.forbiddenIds))
  ) {
    return { valid: false, reason: "artifact canonical query matrix shape or ordering is invalid" };
  }
  if (
    !manifest.provenance?.sourceCatalog
    || !manifest.provenance?.sourceCatalogSha256
    || !manifest.provenance?.generator
    || !manifest.provenance?.modelManifest
    || !manifest.provenance?.transformersJsVersion
  ) {
    return { valid: false, reason: "artifact provenance is incomplete" };
  }
  if (
    manifest.preprocessing?.pooling !== EMBEDDING_CONTRACT.pooling
    || manifest.preprocessing?.normalize !== EMBEDDING_CONTRACT.normalize
    || manifest.preprocessing?.truncation !== EMBEDDING_CONTRACT.truncation
    || manifest.preprocessing?.maxLength !== EMBEDDING_CONTRACT.maxLength
  ) {
    return { valid: false, reason: "artifact preprocessing contract does not match the browser encoder" };
  }
  if (
    (manifest.preprocessing.queryPrefix ?? "") !== (model.queryPrefix ?? "")
    || (manifest.preprocessing.documentPrefix ?? "") !== (model.documentPrefix ?? "")
  ) {
    return { valid: false, reason: "artifact text-prefix contract does not match the browser encoder" };
  }
  return { valid: true, manifest: manifest as PrecomputedIndexManifest };
}

export function canonicalIntegrityCases(records: CatalogInput[]): CanonicalIntegrityCase[] {
  const bySuffix = (suffix: string) => records.find((record) => record.id.endsWith(`:${suffix}`))?.id;
  const headache = bySuffix("headache");
  const neutrophilDecreased = bySuffix("neutrophil-count-decreased");
  const neutrophilIncreased = bySuffix("neutrophil-count-increased");
  const urinaryObstruction = bySuffix("urinary-tract-obstruction");
  const neuropathyIds = records.filter((record) => record.id.includes("neuropathy")).map((record) => record.id);
  const neutrophilIds = [neutrophilDecreased, neutrophilIncreased].filter((id): id is string => Boolean(id));
  const cases: CanonicalIntegrityCase[] = [];
  if (headache) cases.push({ query: "headache", limit: 1, expectedFirstId: headache, requiredIds: [headache], forbiddenIds: [] });
  if (neutrophilDecreased) {
    cases.push({
      query: "neutrophil",
      limit: Math.max(4, neutrophilIds.length),
      requiredIds: neutrophilIds,
      forbiddenIds: [],
    });
    cases.push({
      query: "Neutrophil count",
      limit: Math.max(4, neutrophilIds.length),
      requiredIds: neutrophilIds,
      forbiddenIds: [],
    });
    cases.push({
      query: "Neutrophil count decreased",
      limit: 1,
      expectedFirstId: neutrophilDecreased,
      requiredIds: [neutrophilDecreased],
      forbiddenIds: [],
    });
  }
  if (neuropathyIds.length) {
    cases.push({
      query: "neuropathy",
      limit: 8,
      requiredIds: neuropathyIds,
      forbiddenIds: [],
    });
  }
  if (urinaryObstruction) {
    cases.push({
      query: "Hydronephrosis",
      limit: 8,
      requiredIds: [urinaryObstruction],
      forbiddenIds: [],
    });
  }
  return cases;
}

export function integrityCaseFailure(test: CanonicalIntegrityCase, matches: EncoderMatch[]) {
  const ids = matches.slice(0, test.limit).map((match) => match.id);
  if (test.expectedFirstId && ids[0] !== test.expectedFirstId) {
    return `${JSON.stringify(test.query)} expected ${test.expectedFirstId} first, received ${ids[0] ?? "no result"}`;
  }
  const missing = test.requiredIds.filter((id) => !ids.includes(id));
  if (missing.length) return `${JSON.stringify(test.query)} is missing required top results: ${missing.join(", ")}`;
  const forbidden = test.forbiddenIds.filter((id) => ids.includes(id));
  if (forbidden.length) return `${JSON.stringify(test.query)} returned forbidden version records: ${forbidden.join(", ")}`;
  return undefined;
}

export function rankIndex(
  index: Pick<IndexContract, "ids" | "codes" | "dimensions" | "matrix" | "representationsPerTarget" | "scoring">,
  query: Float32Array,
  limit: number,
): EncoderMatch[] {
  const matrix = asFloat32Array(index.matrix);
  if (!matrix || query.length !== index.dimensions || index.ids.length !== index.codes.length) return [];
  if (query.some((value) => !Number.isFinite(value))) return [];
  const representations = index.representationsPerTarget ?? 1;
  let preparedQuery = query;
  if (index.scoring) {
    const alignment = asFloat32Array(index.scoring.queryAlignment);
    if (!alignment || alignment.length !== index.dimensions * index.dimensions) return [];
    preparedQuery = new Float32Array(index.dimensions);
    let normSquared = 0;
    for (let output = 0; output < index.dimensions; output += 1) {
      let value = 0;
      for (let input = 0; input < index.dimensions; input += 1) {
        value += query[input] * alignment[input * index.dimensions + output];
      }
      preparedQuery[output] = value;
      normSquared += value * value;
    }
    if (!(normSquared > 1e-12)) return [];
    const inverseNorm = 1 / Math.sqrt(normSquared);
    for (let column = 0; column < index.dimensions; column += 1) preparedQuery[column] *= inverseNorm;
  }
  const weights = index.scoring ? asFloat32Array(index.scoring.residualWeights) : undefined;
  const scores = index.codes.map((code, row) => {
    const similarities = new Float64Array(representations);
    for (let representation = 0; representation < representations; representation += 1) {
      const offset = (row * representations + representation) * index.dimensions;
      for (let column = 0; column < index.dimensions; column += 1) {
        similarities[representation] += preparedQuery[column] * matrix[offset + column];
      }
    }
    let score = similarities[0];
    if (index.scoring && weights) {
      const temperature = index.scoring.prototypeTemperature;
      let maximum = Number.NEGATIVE_INFINITY;
      for (let representation = 1; representation < representations; representation += 1) {
        maximum = Math.max(maximum, similarities[representation] / temperature);
      }
      let exponentialSum = 0;
      for (let representation = 1; representation < representations; representation += 1) {
        exponentialSum += Math.exp(similarities[representation] / temperature - maximum);
      }
      const smooth = temperature * (maximum + Math.log(exponentialSum) - Math.log(representations - 1));
      score = (1 - weights[row]) * similarities[0] + weights[row] * smooth;
    }
    return { id: index.ids[row], code, score, ordinal: row };
  });
  scores.sort((left, right) => right.score - left.score || left.ordinal - right.ordinal);
  return scores.slice(0, limit).map(({ id, code, score }) => ({ id, code, score }));
}

export type FusedSearchCandidate = {
  record: CtcaeTerm;
  lexical?: SearchResult;
  lexicalRank?: number;
  semantic?: EncoderMatch;
  semanticRank?: number;
};

function lexicalAuthority(result?: SearchResult) {
  if (!result) return 0;
  if (result.matchType === "exact" || result.matchType === "source-synonym" || result.matchType === "structured-field") return 3;
  if (
    result.matchType === "prefix"
    || result.matchType === "term-token"
    || result.matchType === "fuzzy"
    || (result.matchType === "semantic" && result.score >= 85)
  ) return 2;
  return 0;
}

/** Merge model candidates without ever allowing a model-generated id/code to
 * escape the loaded, version-specific source catalog. Direct canonical-title
 * evidence may lead; otherwise encoder similarity remains the primary order
 * and definition/note evidence follows rather than reordering it. */
export function fuseSearchCandidates(
  records: CtcaeTerm[],
  lexical: SearchResult[],
  semantic: EncoderMatch[],
  limit: number,
): FusedSearchCandidate[] {
  const recordsById = new Map(records.map((record) => [record.id, record]));
  const rows = new Map<string, FusedSearchCandidate>();
  lexical.forEach((result, index) => {
    const record = recordsById.get(result.record.id);
    if (!record || record.code !== result.record.code) return;
    rows.set(record.id, {
      record,
      lexical: result,
      lexicalRank: index + 1,
    });
  });
  semantic.forEach((match, index) => {
    const record = recordsById.get(match.id);
    if (!record || record.code !== match.code) return;
    const current = rows.get(record.id);
    rows.set(record.id, {
      record,
      lexical: current?.lexical,
      lexicalRank: current?.lexicalRank,
      semantic: match,
      semanticRank: index + 1,
    });
  });
  return [...rows.values()]
    .sort((left, right) => {
      const authorityDifference = lexicalAuthority(right.lexical) - lexicalAuthority(left.lexical);
      if (authorityDifference) return authorityDifference;
      const authority = lexicalAuthority(left.lexical);
      if (authority) {
        const lexicalDifference = (right.lexical?.score ?? 0) - (left.lexical?.score ?? 0);
        if (Math.abs(lexicalDifference) > 1e-9) return lexicalDifference;
      }
      if (Boolean(left.semantic) !== Boolean(right.semantic)) return right.semantic ? 1 : -1;
      const leftEvidence = left.semantic?.score ?? (left.lexical?.score ?? 0) / 100;
      const rightEvidence = right.semantic?.score ?? (right.lexical?.score ?? 0) / 100;
      return rightEvidence - leftEvidence
        || (left.semanticRank ?? Number.MAX_SAFE_INTEGER) - (right.semanticRank ?? Number.MAX_SAFE_INTEGER)
        || (left.lexicalRank ?? Number.MAX_SAFE_INTEGER) - (right.lexicalRank ?? Number.MAX_SAFE_INTEGER);
    })
    .slice(0, limit);
}

export function mergeSearchResults(
  records: CtcaeTerm[],
  lexical: SearchResult[],
  semantic: EncoderMatch[],
  limit: number,
) {
  return fuseSearchCandidates(records, lexical, semantic, limit)
    .map((row): SearchResult => {
      const leadingLexical = lexicalAuthority(row.lexical) > 0 || !row.semantic ? row.lexical : undefined;
      return {
        record: row.record,
        matchType: leadingLexical?.matchType ?? "semantic",
        score: leadingLexical?.score ?? 50 + Math.max(-1, Math.min(1, row.semantic?.score ?? 0)) * 25,
      };
    });
}
